import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Upload01, XCircle, ChevronDown, ChevronUp } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { useTranslation } from 'react-i18next';
import { useCreateKnowledgeBase, useUploadKnowledgeDocument } from '@/hooks/api/use-knowledge-bases';
import type { KnowledgeBaseCreate, KnowledgeDocumentAccessLevel, KnowledgeDocumentUploadOptions } from '@/types/api/knowledge-bases';
import type { KnowledgeBaseType } from '@/types/knowledge-base';
import { useToast } from '@/hooks/use-toast';

export const KnowledgeBaseNewPage = () => {
    const { t } = useTranslation(['pages:knowledge-admin', 'common']);
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        kb_type: '' as KnowledgeBaseType | '',
    });
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [uploadOptions, setUploadOptions] = useState<KnowledgeDocumentUploadOptions>({
        priority: undefined,
        access_level: undefined,
        chunking_params: undefined,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    // API hooks
    const createKnowledgeBase = useCreateKnowledgeBase();
    const uploadDocument = useUploadKnowledgeDocument();

    const typeOptions = [
        { id: 'general', label: t('types.general') },
        { id: 'documents', label: t('types.documents') },
        { id: 'routes', label: t('types.routes') },
        { id: 'regulation', label: t('types.regulation') },
        { id: 'company/account-specific', label: t('types.companyAccountSpecific') },
    ];

    const priorityOptions = [
        { id: '1', label: '1 - ' + t('form.documents.priority.lowest', { defaultValue: 'Lowest' }) },
        { id: '2', label: '2 - ' + t('form.documents.priority.low', { defaultValue: 'Low' }) },
        { id: '3', label: '3 - ' + t('form.documents.priority.medium', { defaultValue: 'Medium' }) },
        { id: '4', label: '4 - ' + t('form.documents.priority.high', { defaultValue: 'High' }) },
        { id: '5', label: '5 - ' + t('form.documents.priority.highest', { defaultValue: 'Highest' }) },
    ];

    const accessLevelOptions = [
        { id: 'public', label: t('form.documents.accessLevel.public', { defaultValue: 'Public' }) },
        { id: 'company', label: t('form.documents.accessLevel.company', { defaultValue: 'Company' }) },
        { id: 'admin', label: t('form.documents.accessLevel.admin', { defaultValue: 'Admin' }) },
    ];

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        setUploadFiles([...uploadFiles, ...files]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadFiles([...uploadFiles, ...files]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadFiles(uploadFiles.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.kb_type) {
            toast.error(t('form.validationError', { defaultValue: 'Nome e tipo sono obbligatori' }));
            return;
        }

        try {
            // Create knowledge base
            const createData: KnowledgeBaseCreate = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                kb_type: formData.kb_type as KnowledgeBaseType,
            };

            const newKB = await createKnowledgeBase.mutateAsync(createData);

            // Upload documents if any
            if (uploadFiles.length > 0) {
                // Build options object only if any values are set
                const hasOptions = uploadOptions.priority !== undefined ||
                    uploadOptions.access_level !== undefined ||
                    uploadOptions.chunking_params?.chunk_size !== undefined ||
                    uploadOptions.chunking_params?.chunk_overlap !== undefined;

                for (const file of uploadFiles) {
                    await uploadDocument.mutateAsync({
                        knowledgeBaseId: newKB.knowledge_base_id,
                        file,
                        options: hasOptions ? uploadOptions : undefined,
                    });
                }
            }

            toast.success(t('form.createSuccess', { defaultValue: 'Knowledge base creata con successo' }));
            navigate(`/admin/knowledge/${newKB.knowledge_base_id}`);
        } catch (error) {
            console.error('Error creating knowledge base:', error);
            toast.error(t('form.createError', { defaultValue: 'Errore nella creazione della knowledge base' }));
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('form.new')}</h2>
                    <p className="text-tertiary mt-1">{t('subtitle')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/knowledge')}
                    size="md"
                    color="secondary"
                    iconLeading={ArrowLeft}
                >
                    {t('form.cancel')}
                </Button>
            </div>

            {/* Form */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                    <Input
                        label={t('form.fields.name')}
                        placeholder={t('form.fields.namePlaceholder')}
                        value={formData.name}
                        onChange={(value) => setFormData({ ...formData, name: value })}
                        isRequired
                        size="md"
                    />

                    <Input
                        label={t('form.fields.description')}
                        placeholder={t('form.fields.descriptionPlaceholder')}
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        size="md"
                    />

                    <Select
                        label={t('form.fields.type')}
                        placeholder={t('form.fields.typePlaceholder')}
                        items={typeOptions}
                        selectedKey={formData.kb_type}
                        onSelectionChange={(key) => setFormData({ ...formData, kb_type: key as KnowledgeBaseType | '' })}
                        isRequired
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {/* Documents Upload */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">{t('form.documents.title')}</h3>

                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-brand bg-brand/10' : 'border-secondary'
                            }`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <Upload01 className="size-12 mx-auto mb-4 text-tertiary" />
                        <p className="text-primary mb-2">{t('form.documents.uploadArea')}</p>
                        <p className="text-sm text-tertiary mb-4">{t('form.documents.supportedFormats')}</p>
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            size="md"
                            color="secondary"
                        >
                            {t('form.documents.selectFile')}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.docx,.txt,.md,.json"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                    </div>

                    {/* Selected Files */}
                    {uploadFiles.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-primary">{t('form.documents.selectedFiles')}</label>
                            <div className="space-y-1">
                                {uploadFiles.map((file, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between rounded-lg bg-secondary p-2"
                                    >
                                        <span className="text-sm text-primary">{file.name}</span>
                                        <button
                                            onClick={() => handleRemoveFile(index)}
                                            className="text-error hover:text-error_hover"
                                        >
                                            <XCircle className="size-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Advanced Options */}
                    {uploadFiles.length > 0 && (
                        <div className="border border-secondary rounded-lg">
                            <button
                                type="button"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="w-full flex items-center justify-between p-3 text-left hover:bg-secondary/50 transition-colors rounded-lg"
                            >
                                <span className="text-sm font-medium text-primary">
                                    {t('form.documents.advancedOptions', { defaultValue: 'Advanced Options' })}
                                </span>
                                {showAdvanced ? (
                                    <ChevronUp className="size-4 text-tertiary" />
                                ) : (
                                    <ChevronDown className="size-4 text-tertiary" />
                                )}
                            </button>

                            {showAdvanced && (
                                <div className="p-4 pt-0 space-y-4 border-t border-secondary">
                                    <p className="text-xs text-tertiary mt-2">
                                        {t('form.documents.advancedOptionsHint', { defaultValue: 'These settings apply to all selected files. Leave empty to use defaults.' })}
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Select
                                            label={t('form.documents.priorityLabel', { defaultValue: 'Priority' })}
                                            placeholder={t('form.documents.priorityPlaceholder', { defaultValue: 'Select priority (optional)' })}
                                            items={priorityOptions}
                                            selectedKey={uploadOptions.priority?.toString() || ''}
                                            onSelectionChange={(key) => setUploadOptions({
                                                ...uploadOptions,
                                                priority: key ? parseInt(key as string) : undefined,
                                            })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Select
                                            label={t('form.documents.accessLevelLabel', { defaultValue: 'Access Level' })}
                                            placeholder={t('form.documents.accessLevelPlaceholder', { defaultValue: 'Select access level (optional)' })}
                                            items={accessLevelOptions}
                                            selectedKey={uploadOptions.access_level || ''}
                                            onSelectionChange={(key) => setUploadOptions({
                                                ...uploadOptions,
                                                access_level: key ? key as KnowledgeDocumentAccessLevel : undefined,
                                            })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-primary">
                                            {t('form.documents.chunkingParams', { defaultValue: 'Chunking Parameters' })}
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                label={t('form.documents.chunkSize', { defaultValue: 'Chunk Size' })}
                                                placeholder={t('form.documents.chunkSizePlaceholder', { defaultValue: 'e.g., 1000' })}
                                                type="number"
                                                value={uploadOptions.chunking_params?.chunk_size?.toString() || ''}
                                                onChange={(value) => setUploadOptions({
                                                    ...uploadOptions,
                                                    chunking_params: {
                                                        ...uploadOptions.chunking_params,
                                                        chunk_size: value ? parseInt(value) : undefined,
                                                    },
                                                })}
                                                size="md"
                                            />
                                            <Input
                                                label={t('form.documents.chunkOverlap', { defaultValue: 'Chunk Overlap' })}
                                                placeholder={t('form.documents.chunkOverlapPlaceholder', { defaultValue: 'e.g., 200' })}
                                                type="number"
                                                value={uploadOptions.chunking_params?.chunk_overlap?.toString() || ''}
                                                onChange={(value) => setUploadOptions({
                                                    ...uploadOptions,
                                                    chunking_params: {
                                                        ...uploadOptions.chunking_params,
                                                        chunk_overlap: value ? parseInt(value) : undefined,
                                                    },
                                                })}
                                                size="md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-4 pt-4 border-t border-secondary">
                    <Button
                        onClick={() => navigate('/admin/knowledge')}
                        size="md"
                        color="secondary"
                    >
                        {t('form.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        size="md"
                        color="primary"
                        isDisabled={!formData.name.trim() || !formData.kb_type || createKnowledgeBase.isPending || uploadDocument.isPending}
                    >
                        {(createKnowledgeBase.isPending || uploadDocument.isPending) ? t('loading') : t('form.create')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
